import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DiamondIcon from '@mui/icons-material/Diamond';
import EventIcon from '@mui/icons-material/Event';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import NotesIcon from '@mui/icons-material/Notes';
import PersonIcon from '@mui/icons-material/Person';
import RuleIcon from '@mui/icons-material/Rule';
import ShortTextIcon from '@mui/icons-material/ShortText';
import TaskIcon from '@mui/icons-material/Task';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { IIssue, IssuePriority, IssueType } from '@pulse/shared';
import {
  Alert,
  Button,
  Checkbox,
  DateField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ICON_SIZE,
  LabelWithIcon,
  MarkdownField,
  MenuItem,
  SelectWithIcon,
  Stack,
  TextField,
} from '@pulse/ui';
import { useState } from 'react';

import { EntityPicker } from '../../../components/EntityPicker';
import { useAuth } from '../../../context/AuthContext';
import { blockNegativeHourKeys, restrictedHoursChange, isValidHours } from '../../../lib/hours-input.util';
import { useUserLookup } from '../../access-management/hooks/useUsers';
import { useChangeRequestLookup } from '../hooks/useChangeRequests';
import { useCreateIssue } from '../hooks/useIssues';
import { useMilestones } from '../hooks/useMilestones';
import { useProjectModules } from '../hooks/useProjectModules';
import { useSprints } from '../hooks/useSprints';

const ISSUE_TYPES: IssueType[] = ['Story', 'Defect', 'Task'];
const ISSUE_PRIORITIES: IssuePriority[] = ['Critical', 'High', 'Medium', 'Low'];
const NAME_MAX = 1000;
const DESC_MAX = 6000;

interface IssueFormDialogProps {
  projectId: string;
  /** Pass the parent Issue to create a Sub-Issue (§9d); null for a top-level issue. */
  parentIssue: IIssue | null;
  onClose: () => void;
  onSaved: (issue: IIssue) => void;
  onError: (message: string) => void;
}

export function IssueFormDialog({ projectId, parentIssue, onClose, onSaved, onError }: IssueFormDialogProps) {
  const { data: modules = [] } = useProjectModules(projectId);
  // A deactivated module can't be assigned to a (new) issue — same "still displays an existing
  // assignment, but not selectable for anything else" rule as the closed/inactive Sprint filter
  // just below. This dialog is create-only, so there's no pre-existing selection to preserve.
  const assignableModules = modules.filter((m) => m.isActive);
  // Only an Approved CR can be tagged to a new Task — the API applies the configured Approved
  // step itself (CHANGE_REQUEST_APPROVAL_STEP), so nothing here compares approvalStatus. Same
  // "not offered for anything new" rule as assignableModules above; this dialog is create-only,
  // so there's no existing selection to preserve once a CR moves out of Approved.
  const { data: approvedChangeRequests = [] } = useChangeRequestLookup(projectId);
  // EntityPicker's option label always reads `.name`, so the crNumber prefix is folded into a
  // synthetic display name here rather than left for a custom renderOption — same approach as
  // IssueDetailDialog.tsx/IssueLinkPage.tsx's Change Request picker.
  const changeRequestPickerOptions = approvedChangeRequests.map((cr) => ({ id: cr.id, name: `${cr.crNumber} — ${cr.name}` }));
  const { data: users = [] } = useUserLookup();
  const { user } = useAuth();
  const canSeeMilestones = user?.allScopePermissions.includes('project_finance:read') ?? false;
  // read shows the row; write is what allows setting it — same split as IssueDetailDialog.
  const canEditMilestones = user?.allScopePermissions.includes('project_finance:write') ?? false;
  const { data: milestones = [] } = useMilestones(projectId, canSeeMilestones);
  const { data: sprints = [] } = useSprints(projectId);
  const assignableSprints = sprints.filter((s) => s.isActive && !s.isClosed);
  const createMutation = useCreateIssue();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<IssueType>('Task');
  const [priority, setPriority] = useState<IssuePriority | ''>('');
  const [moduleId, setModuleId] = useState('');
  const [changeRequestId, setChangeRequestId] = useState('');
  const [estimate, setEstimate] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [isMilestoneTrigger, setIsMilestoneTrigger] = useState(false);
  const [paymentMilestoneId, setPaymentMilestoneId] = useState('');
  const [fundingMilestoneId, setFundingMilestoneId] = useState('');
  const [sprintId, setSprintId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [startDateFieldValid, setStartDateFieldValid] = useState(true);
  const [dueDateFieldValid, setDueDateFieldValid] = useState(true);

  const descOverLimit = description.length > DESC_MAX;
  const dueBeforeStart = Boolean(startDate) && Boolean(dueDate) && dueDate < startDate;
  const estimateInvalid = estimate !== '' && !isValidHours(estimate);
  const isValid =
    name.trim().length > 0 &&
    name.length <= NAME_MAX &&
    !descOverLimit &&
    !dueBeforeStart &&
    !estimateInvalid &&
    startDateFieldValid &&
    dueDateFieldValid;

  function handleSave() {
    createMutation.mutate(
      {
        projectId,
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
          type,
          priority: priority || undefined,
          moduleId: moduleId || undefined,
          changeRequestId: changeRequestId || undefined,
          originalEstimate: estimate ? Number(estimate) : undefined,
          parentIssueId: parentIssue?.id,
          ownerId: ownerId || undefined,
          dueDate: dueDate || undefined,
          startDate: startDate || undefined,
          isMilestoneTrigger: canEditMilestones ? isMilestoneTrigger : undefined,
          paymentMilestoneId: canEditMilestones && isMilestoneTrigger ? paymentMilestoneId || undefined : undefined,
          fundingMilestoneId: canEditMilestones && isMilestoneTrigger ? fundingMilestoneId || undefined : undefined,
          sprintId: sprintId || undefined,
        },
      },
      {
        onSuccess: (issue) => onSaved(issue),
        onError: (err: Error) => { setFormError(err.message); onError(err.message); },
      },
    );
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{parentIssue ? `Add Sub-Issue to ${parentIssue.issueKey}` : 'Create Issue'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField
            label={<LabelWithIcon icon={<ShortTextIcon fontSize={ICON_SIZE.sm} />} label="Name" inline />}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
            error={name.length > NAME_MAX}
            helperText={name.length > NAME_MAX ? `${name.length}/${NAME_MAX} — ${name.length - NAME_MAX} characters over the limit` : `${name.length}/${NAME_MAX}`}
          />
          <MarkdownField
            label={<LabelWithIcon icon={<NotesIcon fontSize={ICON_SIZE.sm} />} label="Description" inline />}
            value={description}
            onChange={setDescription}
            error={descOverLimit}
            helperText={descOverLimit ? `${description.length}/${DESC_MAX} — ${description.length - DESC_MAX} characters over the limit` : `${description.length}/${DESC_MAX}`}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
              <SelectWithIcon icon={<TaskIcon fontSize={ICON_SIZE.sm} />} label="Type" value={type} onChange={(v) => setType(v as IssueType)}>
                {ISSUE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </SelectWithIcon>
              <SelectWithIcon icon={<WarningAmberIcon fontSize={ICON_SIZE.sm} />} label="Priority" value={priority} onChange={(v) => setPriority(v as IssuePriority)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {ISSUE_PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </SelectWithIcon>
            </Stack>
            <TextField
              label={<LabelWithIcon icon={<AccessTimeIcon fontSize={ICON_SIZE.sm} />} label="Estimate(h)" inline />}
              type="number"
              value={estimate}
              onChange={restrictedHoursChange(setEstimate)}
              onKeyDown={blockNegativeHourKeys}
              slotProps={{ htmlInput: { min: 0, step: 'any' } }}
              error={estimateInvalid}
              helperText={estimateInvalid ? 'Enter a valid number of hours.' : ' '}
              sx={{ width: { xs: '100%', sm: 160 }, flexShrink: 0 }}
            />
          </Stack>
          {!parentIssue && (
            <Stack direction="row" spacing={2}>
              <EntityPicker
                label="Module"
                labelIcon={<ViewModuleIcon sx={{ fontSize: ICON_SIZE.xs, verticalAlign: 'middle', mr: 0.5, position: 'relative', top: '-1px' }} />}
                placeholder="None"
                hideAvatar
                noOptionClamp
                options={assignableModules}
                value={assignableModules.find((m) => m.id === moduleId) ?? null}
                onChange={(m) => setModuleId(m?.id ?? '')}
              />
              <EntityPicker
                label="Sprint"
                labelIcon={<EventRepeatIcon sx={{ fontSize: ICON_SIZE.xs, verticalAlign: 'middle', mr: 0.5, position: 'relative', top: '-1px' }} />}
                placeholder="None"
                hideAvatar
                noOptionClamp
                options={assignableSprints}
                value={assignableSprints.find((s) => s.id === sprintId) ?? null}
                onChange={(s) => setSprintId(s?.id ?? '')}
              />
            </Stack>
          )}
          <EntityPicker
            label="Change Request"
            labelIcon={<RuleIcon sx={{ fontSize: ICON_SIZE.xs, verticalAlign: 'middle', mr: 0.5, position: 'relative', top: '-1px' }} />}
            placeholder="None"
            hideAvatar
            noOptionClamp
            options={changeRequestPickerOptions}
            value={changeRequestPickerOptions.find((cr) => cr.id === changeRequestId) ?? null}
            onChange={(cr) => setChangeRequestId(cr?.id ?? '')}
          />
          <EntityPicker
            label="Owner"
            labelIcon={<PersonIcon sx={{ fontSize: ICON_SIZE.xs, verticalAlign: 'middle', mr: 0.5, position: 'relative', top: '-1px' }} />}
            options={users}
            placeholder="No Owner"
            value={users.find((u) => u.id === ownerId) ?? null}
            onChange={(u) => setOwnerId(u?.id ?? '')}
          />
          {canSeeMilestones && (
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isMilestoneTrigger}
                    disabled={!canEditMilestones}
                    onChange={(e) => setIsMilestoneTrigger(e.target.checked)}
                  />
                }
                label="Milestone trigger"
              />
              {isMilestoneTrigger && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <SelectWithIcon icon={<DiamondIcon fontSize={ICON_SIZE.sm} />} label="Payment Milestone" value={paymentMilestoneId} onChange={setPaymentMilestoneId}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {milestones.filter((m) => m.type === 'Payment').map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                  </SelectWithIcon>
                  <SelectWithIcon icon={<DiamondIcon fontSize={ICON_SIZE.sm} />} label="Funding Milestone" value={fundingMilestoneId} onChange={setFundingMilestoneId}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {milestones.filter((m) => m.type === 'Funding').map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                  </SelectWithIcon>
                </Stack>
              )}
            </Stack>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <DateField
              label={<LabelWithIcon icon={<EventIcon fontSize={ICON_SIZE.sm} />} label="Start Date" inline />}
              value={startDate}
              onChange={setStartDate}
              onValidityChange={setStartDateFieldValid}
              invalidMessage="Please enter a valid Start Date."
              fullWidth
            />
            <DateField
              label={<LabelWithIcon icon={<EventIcon fontSize={ICON_SIZE.sm} />} label="Due Date" inline />}
              value={dueDate}
              onChange={setDueDate}
              onValidityChange={setDueDateFieldValid}
              invalidMessage="Please enter a valid Due Date."
              fullWidth
              error={dueBeforeStart}
              helperText={dueBeforeStart ? 'Due Date cannot be before Start Date.' : ' '}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={createMutation.isPending}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!isValid || createMutation.isPending}>
          {createMutation.isPending ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
